import React, { useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Pill,
  Search,
  Sparkles,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, {
  imgUrl,
  eur,
  formatApiErrorDetail,
} from "../../lib/api";
import { MultiImageUpload } from "../../components/admin/ImageUpload";

const empty = {
  name: "",
  description: "",
  price: "",
  compare_price: "",
  category_id: "",
  category_ids: [],
  images: [],
  stock: 0,
  sku: "",
  brand: "",
  active: true,
  featured: false,
  prescription_required: false,

  // Αρχική σελίδα
  new_product: false,
  best_seller: false,
  gift_moment: false,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    api
      .get("/products", {
        params: { admin_view: true },
      })
      .then(({ data }) => setProducts(data))
      .catch(() => {});

    api
      .get("/categories")
      .then(({ data }) => setCategories(data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      ...empty,
      category_ids: [],
    });
    setOpen(true);
  };

  const openEdit = (p) => {
    const existingCategoryIds =
      Array.isArray(p.category_ids) && p.category_ids.length
        ? p.category_ids
        : p.category_id
          ? [p.category_id]
          : [];

    setEditing(p);

    setForm({
      ...empty,
      ...p,
      price: p.price,
      compare_price: p.compare_price ?? "",
      category_id: existingCategoryIds[0] || "",
      category_ids: existingCategoryIds,

      // Αρχική σελίδα
      new_product: !!p.new_product,
      best_seller: !!p.best_seller,
      gift_moment: !!p.gift_moment,
    });

    setOpen(true);
  };

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.value,
    }));

  const setBool = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.checked,
    }));

  const toggleCategory = (categoryId) => {
    setForm((f) => {
      const current = Array.isArray(f.category_ids)
        ? f.category_ids
        : [];

      const next = current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId];

      return {
        ...f,
        category_ids: next,
        category_id: next[0] || "",
      };
    });
  };

  const save = async (e) => {
    e.preventDefault();

    if (!form.name || form.price === "") {
      toast.error("Συμπληρώστε όνομα και τιμή");
      return;
    }

    setSaving(true);

    const categoryIds = Array.isArray(form.category_ids)
      ? form.category_ids
      : form.category_id
        ? [form.category_id]
        : [];

    const payload = {
      ...form,

      category_ids: categoryIds,
      category_id: categoryIds[0] || "",

      price: parseFloat(form.price),

      compare_price:
        form.compare_price === ""
          ? null
          : parseFloat(form.compare_price),

      stock: parseInt(form.stock) || 0,

      // Αρχική σελίδα
      new_product: !!form.new_product,
      best_seller: !!form.best_seller,
      gift_moment: !!form.gift_moment,
    };

    delete payload.id;
    delete payload.slug;
    delete payload.created_at;

    try {
      if (editing) {
        await api.put(
          `/products/${editing.id}`,
          payload
        );
      } else {
        await api.post(
          "/products",
          payload
        );
      }

      toast.success(
        editing
          ? "Το προϊόν ενημερώθηκε"
          : "Το προϊόν προστέθηκε"
      );

      setOpen(false);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Σφάλμα"
      );
    } finally {
      setSaving(false);
    }
  };

  const generateDesc = async () => {
    if (!form.name) {
      toast.error(
        "Συμπληρώστε πρώτα το όνομα του προϊόντος"
      );
      return;
    }

    setAiLoading(true);

    try {
      const selectedCategoryId =
        form.category_ids?.[0] ||
        form.category_id;

      const catName =
        categories.find(
          (c) => c.id === selectedCategoryId
        )?.name || "";

      const { data } = await api.post(
        "/ai/generate-description",
        {
          name: form.name,
          brand: form.brand,
          category: catName,
        }
      );

      setForm((f) => ({
        ...f,
        description: data.description,
      }));

      toast.success(
        "Η περιγραφή δημιουργήθηκε με AI"
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Αποτυχία δημιουργίας"
      );
    } finally {
      setAiLoading(false);
    }
  };

  const del = async (p) => {
    if (
      !window.confirm(
        `Διαγραφή του «${p.name}»;`
      )
    ) {
      return;
    }

    try {
      await api.delete(
        `/products/${p.id}`
      );

      toast.success("Διαγράφηκε");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Σφάλμα"
      );
    }
  };

  const filtered = products.filter((p) =>
    p.name
      .toLowerCase()
      .includes(q.toLowerCase())
  );

  const inputCls =
    "w-full px-3 py-2.5 rounded-sm border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none";

  const mainCategories =
    categories.filter(
      (c) =>
        !c.parent_id ||
        c.parent_id === ""
    );

  const getSubcategories = (parentId) =>
    categories.filter(
      (c) => c.parent_id === parentId
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-serif-display text-3xl text-[#1E3A2F]">
          Προϊόντα
        </h1>

        <button
          onClick={openNew}
          data-testid="add-product-button"
          className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#111C18]"
        >
          <Plus className="h-4 w-4" />
          Νέο προϊόν
        </button>
      </div>

      <div className="relative mb-4 max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#536D61]" />

        <input
          value={q}
          onChange={(e) =>
            setQ(e.target.value)
          }
          placeholder="Αναζήτηση..."
          className={`${inputCls} pl-9`}
        />
      </div>

      <div className="bg-white border border-[#E5E9E4] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#536D61] border-b border-[#E5E9E4]">
              <th className="py-3 px-4 font-medium">
                Προϊόν
              </th>

              <th className="py-3 px-4 font-medium">
                Τιμή
              </th>

              <th className="py-3 px-4 font-medium">
                Απόθεμα
              </th>

              <th className="py-3 px-4 font-medium">
                Κατάσταση
              </th>

              <th className="py-3 px-4 font-medium text-right">
                Ενέργειες
              </th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((p) => (
              <tr
                key={p.id}
                className="border-b border-[#F0F2EE] hover:bg-[#F9F8F6]"
                data-testid={`product-row-${p.id}`}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-sm overflow-hidden bg-[#E8F0E1] shrink-0 flex items-center justify-center">
                      {p.images?.[0] ? (
                        <img
                          src={imgUrl(
                            p.images[0]
                          )}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Pill className="h-5 w-5 text-[#1E3A2F]/40" />
                      )}
                    </div>

                    <div className="min-w-0">
                      <p className="font-medium text-[#1E3A2F] truncate max-w-[200px]">
                        {p.name}
                      </p>

                      {p.brand && (
                        <p className="text-xs text-[#536D61]">
                          {p.brand}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                <td className="py-3 px-4">
                  {eur(p.price)}
                </td>

                <td className="py-3 px-4">
                  {p.stock}
                </td>

                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-sm ${
                        p.active
                          ? "bg-[#E8F0E1] text-[#4A5D23]"
                          : "bg-[#f3e3e2] text-[#C25953]"
                      }`}
                    >
                      {p.active
                        ? "Ενεργό"
                        : "Ανενεργό"}
                    </span>

                    {p.featured && (
                      <span className="text-xs px-2 py-0.5 rounded-sm bg-[#1E3A2F] text-white">
                        Προβολή
                      </span>
                    )}

                    {p.new_product && (
                      <span className="text-xs px-2 py-0.5 rounded-sm bg-[#E8F0E1] text-[#1E3A2F]">
                        Νέο
                      </span>
                    )}

                    {p.best_seller && (
                      <span className="text-xs px-2 py-0.5 rounded-sm bg-[#F7F1EB] text-[#7A5C45]">
                        Best Seller
                      </span>
                    )}

                    {p.gift_moment && (
                      <span className="text-xs px-2 py-0.5 rounded-sm bg-[#FFF4F2] text-[#C25953]">
                        Gift
                      </span>
                    )}
                  </div>
                </td>

                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() =>
                        openEdit(p)
                      }
                      data-testid={`edit-product-${p.id}`}
                      className="p-2 text-[#536D61] hover:text-[#1E3A2F]"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => del(p)}
                      data-testid={`delete-product-${p.id}`}
                      className="p-2 text-[#C25953] hover:text-[#a3433d]"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="py-12 text-center text-[#536D61]"
                >
                  Δεν υπάρχουν προϊόντα.
                  Προσθέστε το πρώτο σας προϊόν.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto bg-black/50">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E9E4]">
              <h2 className="font-serif-display text-2xl text-[#1E3A2F]">
                {editing
                  ? "Επεξεργασία προϊόντος"
                  : "Νέο προϊόν"}
              </h2>

              <button
                onClick={() =>
                  setOpen(false)
                }
              >
                <X className="h-5 w-5 text-[#536D61]" />
              </button>
            </div>

            <form
              onSubmit={save}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                  Όνομα *
                </label>

                <input
                  data-testid="product-name"
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                    Τιμή (€) *
                  </label>

                  <input
                    data-testid="product-price"
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={set("price")}
                    className={inputCls}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                    Τιμή σύγκρισης (€)
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    value={form.compare_price}
                    onChange={set(
                      "compare_price"
                    )}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                    Απόθεμα
                  </label>

                  <input
                    data-testid="product-stock"
                    type="number"
                    value={form.stock}
                    onChange={set("stock")}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                    Μάρκα
                  </label>

                  <input
                    value={form.brand}
                    onChange={set("brand")}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                    Κωδικός (SKU)
                  </label>

                  <input
                    value={form.sku}
                    onChange={set("sku")}
                    className={inputCls}
                  />
                </div>

                {/* ΠΟΛΛΑΠΛΕΣ ΚΑΤΗΓΟΡΙΕΣ */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">
                    Κατηγορίες
                  </label>

                  <div className="border border-[#E5E9E4] rounded-sm bg-white max-h-64 overflow-y-auto p-3">
                    {mainCategories.length === 0 ? (
                      <p className="text-sm text-[#536D61]">
                        Δεν υπάρχουν
                        κατηγορίες ακόμη.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {mainCategories.map(
                          (category) => {
                            const subcategories =
                              getSubcategories(
                                category.id
                              );

                            return (
                              <div
                                key={
                                  category.id
                                }
                              >
                                <label className="flex items-center gap-2 text-sm font-medium text-[#1E3A2F] cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={form.category_ids.includes(
                                      category.id
                                    )}
                                    onChange={() =>
                                      toggleCategory(
                                        category.id
                                      )
                                    }
                                    className="accent-[#1E3A2F]"
                                  />

                                  {category.name}
                                </label>

                                {subcategories.length >
                                  0 && (
                                  <div className="ml-6 mt-2 space-y-2 border-l border-[#E5E9E4] pl-4">
                                    {subcategories.map(
                                      (sub) => (
                                        <label
                                          key={
                                            sub.id
                                          }
                                          className="flex items-center gap-2 text-sm text-[#536D61] cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={form.category_ids.includes(
                                              sub.id
                                            )}
                                            onChange={() =>
                                              toggleCategory(
                                                sub.id
                                              )
                                            }
                                            className="accent-[#1E3A2F]"
                                          />

                                          {
                                            sub.name
                                          }
                                        </label>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-[#536D61] mt-1.5">
                    Μπορείτε να επιλέξετε
                    περισσότερες από μία
                    κατηγορίες για το ίδιο
                    προϊόν.
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-[#1E3A2F]">
                    Περιγραφή
                  </label>

                  <button
                    type="button"
                    onClick={generateDesc}
                    disabled={aiLoading}
                    data-testid="ai-generate-description"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1E3A2F] bg-[#E8F0E1] hover:bg-[#d8e6c9] px-2.5 py-1.5 rounded-full disabled:opacity-60"
                  >
                    {aiLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}

                    Δημιουργία με AI
                  </button>
                </div>

                <textarea
                  value={form.description}
                  onChange={set(
                    "description"
                  )}
                  rows={6}
                  className={inputCls}
                  placeholder="Γράψτε ή δημιουργήστε αυτόματα με AI. Χρησιμοποιήστε επικεφαλίδες όπως «Συστατικά:», «Τρόπος χρήσης:», «Προφυλάξεις:» για αυτόματα tabs στη σελίδα προϊόντος."
                />
              </div>

              <MultiImageUpload
                values={form.images}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    images: v,
                  }))
                }
              />

              {/* ================================================= */}
              {/* ΕΜΦΑΝΙΣΗ / ΑΡΧΙΚΗ ΣΕΛΙΔΑ */}
              {/* ================================================= */}

              <div className="border-t border-[#E5E9E4] pt-4">
                <p className="text-sm font-semibold text-[#1E3A2F] mb-3">
                  Εμφάνιση στην αρχική
                </p>

                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                    <input
                      type="checkbox"
                      checked={!!form.active}
                      onChange={setBool(
                        "active"
                      )}
                      className="accent-[#1E3A2F]"
                      data-testid="product-active"
                    />
                    Ενεργό
                  </label>

                  <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                    <input
                      type="checkbox"
                      checked={!!form.featured}
                      onChange={setBool(
                        "featured"
                      )}
                      className="accent-[#1E3A2F]"
                      data-testid="product-featured"
                    />
                    Προβολή στην αρχική
                  </label>

                  <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                    <input
                      type="checkbox"
                      checked={
                        !!form.new_product
                      }
                      onChange={setBool(
                        "new_product"
                      )}
                      className="accent-[#1E3A2F]"
                      data-testid="product-new"
                    />
                    Νέο προϊόν
                  </label>

                  <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                    <input
                      type="checkbox"
                      checked={
                        !!form.best_seller
                      }
                      onChange={setBool(
                        "best_seller"
                      )}
                      className="accent-[#1E3A2F]"
                      data-testid="product-best-seller"
                    />
                    Best Seller
                  </label>

                  <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                    <input
                      type="checkbox"
                      checked={
                        !!form.gift_moment
                      }
                      onChange={setBool(
                        "gift_moment"
                      )}
                      className="accent-[#1E3A2F]"
                      data-testid="product-gift"
                    />
                    Gift Moment
                  </label>

                  <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                    <input
                      type="checkbox"
                      checked={
                        !!form.prescription_required
                      }
                      onChange={setBool(
                        "prescription_required"
                      )}
                      className="accent-[#1E3A2F]"
                    />
                    Με συνταγή
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E9E4]">
                <button
                  type="button"
                  onClick={() =>
                    setOpen(false)
                  }
                  className="px-4 py-2.5 rounded-sm border border-[#E5E9E4] text-sm"
                >
                  Ακύρωση
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-product-button"
                  className="px-5 py-2.5 rounded-sm bg-[#1E3A2F] text-white text-sm font-medium hover:bg-[#111C18] disabled:opacity-60"
                >
                  {saving
                    ? "Αποθήκευση..."
                    : "Αποθήκευση"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}